import { extractListItems } from 'core/utils/apollo/extractListItems';
import { useGetAnalysedSourcedCandidatesByCandidateBatchIdLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useEffect, useState } from 'react';

type SourcedCandidateItems = {
  candidateBatchId: string;
  candidateProfilesIds: string[];
};

type FetchPageProps = {
  candidateBatchId: string;
  nextToken?: string;
  accumulatedItems: string[];
};

type UseFetchAllSourcedCandidatesByCandidateBatchProps = {
  candidateBatchId: string | null;
};

export const useFetchAllSourcedCandidatesByCandidateBatch = ({
  candidateBatchId
}: UseFetchAllSourcedCandidatesByCandidateBatchProps) => {
  const [getAnalysedSourcedCandidatesByCandidateBatchIdLazyQuery, { loading }] =
    useGetAnalysedSourcedCandidatesByCandidateBatchIdLazyQuery();
  const [sourcedCandidates, setSourcedCandidates] =
    useState<SourcedCandidateItems>();

  const fetchPage = async ({
    candidateBatchId,
    nextToken,
    accumulatedItems
  }: FetchPageProps): Promise<string[]> => {
    const result =
      await getAnalysedSourcedCandidatesByCandidateBatchIdLazyQuery({
        variables: {
          candidateBatchId,
          nextToken
        }
      });

    const items = extractListItems(
      result.data?.getSourcedCandidatesByCandidateBatch?.items ?? []
    )
      .filter((item) => !!item.candidateProfileId)
      // We can safely cast here because we know the item is not null
      .map((item) => item.candidateProfileId as string);

    // If no items were returned, stop pagination
    if (items.length === 0) {
      return accumulatedItems;
    }

    const allItems = [...accumulatedItems, ...items];
    const newNextToken =
      result.data?.getSourcedCandidatesByCandidateBatch?.nextToken;

    // Continue if there's a next token
    if (newNextToken) {
      return fetchPage({
        candidateBatchId,
        nextToken: newNextToken,
        accumulatedItems: allItems
      });
    }

    return allItems;
  };

  useEffect(() => {
    // Capture the current candidateBatchId to detect stale requests
    const activeId = candidateBatchId;

    const fetchAllSourcedCandidates = async () => {
      if (!activeId) {
        setSourcedCandidates(undefined);
        return;
      }

      if (activeId === sourcedCandidates?.candidateBatchId) {
        return;
      }

      const results = await fetchPage({
        candidateBatchId: activeId,
        accumulatedItems: []
      });

      // Only update state if this is still the current candidateBatchId
      if (activeId === candidateBatchId) {
        setSourcedCandidates({
          candidateBatchId: activeId,
          candidateProfilesIds: results
        });
      }
    };

    fetchAllSourcedCandidates();
  }, [candidateBatchId, sourcedCandidates?.candidateBatchId]);

  return {
    sourcedCandidates,
    loading
  };
};
